import React from "react";
import {
  columnsFor,
  COLUMN_HELP,
  NESTING_LEGEND,
  demoRowObject,
} from "../utils/pageCsv";

const TYPE_LABELS = { "grid-guide": "Grid Guide", listicle: "Ranked Listicle" };

/**
 * Explains the bulk-import CSV format for a given page type, showing every
 * column, what it means, and the value used in the demo row — then lets the
 * user download the template.
 */
export default function CsvHelpModal({ isOpen, type, onClose, onDownload }) {
  if (!isOpen) return null;

  const cols = columnsFor(type);
  const demo = demoRowObject(type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {TYPE_LABELS[type] || type} — CSV template
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              One row = one page. Fill each row and re-upload. Every field is optional except{" "}
              <b>slug</b>, <b>type</b> and <b>title</b>. You can still fix everything in the editor
              after uploading.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-4"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          {/* Nesting legend */}
          <h3 className="font-bold text-gray-800 mb-2">Separators for nested content</h3>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-5">
            <ul className="space-y-1 text-sm">
              {NESTING_LEGEND.map(([sym, desc]) => (
                <li key={sym} className="flex gap-3">
                  <code className="font-mono font-bold text-[#4d8a82] w-8 shrink-0 text-center">
                    {sym}
                  </code>
                  <span className="text-gray-600">{desc}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              These 5 characters are reserved — don't use them inside the actual text/URLs.
            </p>
          </div>

          {/* Columns + demo values */}
          <h3 className="font-bold text-gray-800 mb-2">Columns</h3>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-2 font-semibold">Column</th>
                  <th className="text-left p-2 font-semibold">What it is</th>
                  <th className="text-left p-2 font-semibold">Example (from template)</th>
                </tr>
              </thead>
              <tbody>
                {cols.map((c) => (
                  <tr key={c} className="border-t border-gray-100 align-top">
                    <td className="p-2 font-mono font-semibold text-gray-800 whitespace-nowrap">
                      {c}
                    </td>
                    <td className="p-2 text-gray-600">{COLUMN_HELP[c] || ""}</td>
                    <td className="p-2 text-gray-500">
                      <code className="break-all">{demo[c] || <span className="text-gray-300">—</span>}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-5 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
          >
            Close
          </button>
          <button
            onClick={onDownload}
            className="px-5 py-2 rounded-lg bg-[#4d8a82] text-white font-semibold hover:bg-[#3d6e68]"
          >
            Download template CSV
          </button>
        </div>
      </div>
    </div>
  );
}
